import { IObservableArray, computed, flow, makeAutoObservable, observable, reaction, when } from 'mobx';
import type ExplorerRootStore from '../ExplorerRootStore';
import { wait, toGenerator } from '../utils';
import EVMConnector from './EVM';
import SVMConnector from './SVM';
import EVMBase from './Wallets/EVMBase';
import SolanaWallet from './Wallets/SolanaWallet';
import SuiWallet from './Wallets/SuiWallet';
import SUIConnector from './SUI';
import WalletConnectClient from './WalletConnect/Client';

const DISABLE_WALLETCONNECT = typeof process !== 'undefined'
	? process.env.NEXT_PUBLIC_DISABLE_WALLETCONNECT === 'true'
	: false;

export function isSolanaConnector(c: unknown): c is SolanaWallet {
	return c instanceof SolanaWallet;
}

export function isSuiConnector(c: unknown): c is SuiWallet {
	return c instanceof SuiWallet;
}

export enum ConnectorEnum {
	injected = 'injected',
	metamask = 'metamask',
	brave = 'brave',
	backpack = 'backpack',
	coinbase = 'coinbase',
	walletconnect = 'walletconnect',
	phantom = 'phantom',
}

export type AnyWallet = EVMBase | SolanaWallet | SuiWallet;

class Wallets {
	private readonly root: ExplorerRootStore;
	private userUsedWallets: IObservableArray<AnyWallet> = observable([]);
	isWalletReady: boolean = false;
	private defaultChanged = false;

	private _origin: AnyWallet | undefined;
	private _destination: AnyWallet | undefined;

	private _otherRecipient: string | undefined;

	readonly evm: EVMConnector;
	readonly svm: SVMConnector;
	readonly sui: SUIConnector;
	readonly walletconnect: WalletConnectClient | undefined;

	readonly walletconnectEnabled = !DISABLE_WALLETCONNECT;

	constructor(root: ExplorerRootStore) {
		this.root = root;
		if (this.walletconnectEnabled) {
			this.walletconnect = new WalletConnectClient(this.root);
		}
		this.evm = new EVMConnector(this.root);
		this.svm = new SVMConnector(this.root);
		this.sui = new SUIConnector(this.root);

		makeAutoObservable<Wallets, 'root' | 'evmConnectors' | 'userUsedWallets'>(
			this,
			{ evmConnectors: computed, root: false, svm: false, evm: false, sui: false, userUsedWallets: false },
			{ autoBind: true },
		);

		reaction(() => this.readyStatus, this.checkReady);

		reaction(
			() => this.userUsedWallets.filter((c) => !c.connected).length !== 0,
			(disconnected) => {
				if (!disconnected) return;
				this.userUsedWallets.replace(this.userUsedWallets.filter((c) => c.connected));
			},
		);

		let reactionLoopCounter = 0;
		reaction(
			() => this._origin,
			async (wallet, previousWallet) => {
				if (reactionLoopCounter < 3) {
					const currentWallet = this._origin;
					try {
						await this.ensureChain(wallet, 'origin');
					} catch (e) {
						reactionLoopCounter += 1;
						if (currentWallet === this._origin) {
							this.activate(previousWallet || null, 'origin', true);
						}
						throw e;
					}
				} else {
					console.error('Caught in an irrecoverable state, please refresh the page');
				}
				reactionLoopCounter = 0;
			},
		);

		reaction(() => [this._origin, this.originWallet], this.convergeOrigin);

		reaction(
			() => this._destination,
			(wallet) => this.ensureChain(wallet, 'destination'),
		);
	}

	private get readyStatus() {
		if (!this.originWallet) {
			return false;
		}
		if (isSolanaConnector(this.originWallet)) {
			return this.originWallet.isReady;
		}
		if (isSuiConnector(this.originWallet)) {
			return this.originWallet.isReady;
		}
		const activeChain = this.originWallet.chainId;
		return Boolean(
			this.originWallet.isReady && this.root.chains.originEvmChains.find(({ chainId }) => chainId === activeChain),
		);
	}

	private checkReady = flow(function* checkReady(this: Wallets) {
		if (this.readyStatus === this.isWalletReady) {
			return;
		}
		if (!this.readyStatus) {
			yield wait(500);
		}
		this.isWalletReady = this.readyStatus;
	});

	get connectedWallets() {
		const wallets = [...this.evm.wallets, ...this.svm.wallets, ...this.sui.wallets];
		return wallets.filter((w) => w.isReady && w.connected);
	}

	get originWallet() {
		const chain = this.root.chains.originChain;
		if (this._origin?.supports(chain)) {
			return this._origin;
		}
		return this.findReadyFor(chain);
	}

	get destinationWallet() {
		const chain = this.root.chains.destinationChain;
		if (this._destination?.supports(chain)) {
			return this._destination;
		}
		return this.findReadyFor(chain);
	}

	get needsFix() {
		return Boolean(this.originWallet && this.originWallet.chainId !== this.root.chains.originChain.chainId);
	}

	get otherRecipient() {
		return this._otherRecipient;
	}

	setOtherRecipient(value: string | undefined) {
		this._otherRecipient = value;
	}

	nextWalletForChain(chain: Chain, direction: 'from' | 'to') {
		const prev = direction === 'from' ? this._origin : this._destination;
		if (prev?.supports(chain)) {
			return prev;
		}
		return this.findReadyFor(chain);
	}

	get signer() {
		const wallet = this.originWallet;
		if (!wallet) return;
		return 'signer' in wallet ? wallet.signer : undefined;
	}

	activate = flow(function* activate(
		this: Wallets,
		value: 'solana' | 'sui' | 'evm' | EVMBase | SolanaWallet | SuiWallet | null,
		direction: 'origin' | 'destination' | 'anyEvm',
		quick = false,
	) {
		let wallet: AnyWallet | undefined;
		if (value === 'solana') {
			wallet = this.svm.lastUsed;
		} else if (value === 'sui') {
			wallet = this.sui.lastUsed;
		} else if (value === 'evm') {
			wallet = this.evm.lastUsed;
		} else if (value === null) {
			wallet = undefined;
		} else {
			wallet = value;
		}

		if (wallet) {
			switch (wallet.type) {
				case 'evm':
					yield this.evm.connect(wallet, quick || wallet.isReady);
					break;
				case 'solana':
					yield this.svm.connect(wallet);
					break;
				case 'sui':
					yield this.sui.connect(wallet);
					break;
			}
		}

		if (direction === 'anyEvm') {
			if (!this.originWallet) {
				direction = 'origin';
			} else if (!this.destinationWallet) {
				direction = 'destination';
			}
		}

		if (direction === 'origin') {
			this._origin = wallet;
		} else if (direction === 'destination') {
			this._destination = wallet;
		}
		this.defaultChanged = true;

		if (wallet) {
			this.userUsedWallets.remove(wallet);
			this.userUsedWallets.unshift(wallet);
		}
	});

	private convergeOrigin = flow(function* convergeOrigin(this: Wallets) {
		if (this._origin === this.originWallet) return;
		yield wait(3_000);
		if (this._origin === this.originWallet) return;
		this._origin = this.originWallet;
	});

	private ensureChain = flow(function* ensureChain(
		this: Wallets,
		wallet: EVMBase | SolanaWallet | SuiWallet | undefined,
		direction: 'origin' | 'destination',
	) {
		const chains = this.root.chains;
		if (!wallet) return;
		const chain = direction === 'origin' ? chains.originChain : chains.destinationChain;
		if (!wallet.supports(chain)) {
			const newChainId = wallet.chainId ?? wallet.supportedChainIds?.[0];
			if (direction === 'origin') {
				yield chains.setOriginChain(newChainId);
			} else {
				yield chains.setDestinationChain(newChainId);
			}
		}
	});

	private enableLastUsed = flow(function* enableLastUsed(
		this: Wallets,
		wallet: EVMBase | SolanaWallet,
	) {
		const WAIT_THRESHOLD = 5_000;
		try {
			if (isSolanaConnector(wallet)) {
				// Trust wallet doesn't support silent connect properly
			} else if (isSuiConnector(wallet)) {
				//
			} else {
				yield Promise.race([wallet.refresh(), when(() => wallet.isReady, { timeout: WAIT_THRESHOLD })]);
			}
		} catch {
			/* empty */
		}

		if (this.defaultChanged && this.originWallet) {
			return;
		}
		if (wallet.isReady) {
			yield this.activate(wallet, 'origin', true);
		} else {
			console.warn(`Can't activate last used wallet: "${wallet.name}"`);
		}
	});

	activateFirst = flow(function* activateFirst(this: Wallets, lastUsed?: string[] | null) {
		const isOriginSolana = this.root.chains.originChain.mode === 'SOLANA';
		const isOriginSui = this.root.chains.originChain.mode === 'SUI';
		let bypassActivation = isOriginSolana || isOriginSui;
		let lastUsedPromise = Promise.resolve();
		let lastUsedWallet: SolanaWallet | EVMBase | undefined;
		if (lastUsed) {
			const id = lastUsed[0] as 'solana' | 'sui' | 'evm';
			const name = lastUsed[1] ?? id;
			if (id === 'sui') {
				bypassActivation = true;
			} else if (id === 'solana') {
				lastUsedWallet = this.svm.wallets.find((w) => w.name === name);
			} else {
				lastUsedWallet = this.evm.wallets.find((w) => w.name === name);
			}
			if (lastUsedWallet) {
				if (lastUsedWallet.type === 'evm') {
					lastUsedWallet.ensureInjectedProvider();
				}
				lastUsedPromise = this.enableLastUsed(lastUsedWallet);
			}
		}
		const promises: Promise<EVMBase | void>[] = [];

		const delay = wait(500);

		async function load(wallet: EVMBase) {
			try {
				const promise = wallet.refresh();
				await Promise.race([delay, promise]);
				promise.cancel();
				if (wallet.isReady) {
					return wallet;
				}
			} catch {
				/* empty */
			}
			return;
		}

		for (const wallet of this.evm.orderedWallets) {
			if (wallet === lastUsedWallet) continue;
			try {
				if (wallet) {
					promises.push(load(wallet));
				}
			} catch {
				//
			}
		}

		const result = yield* toGenerator(Promise.all(promises));

		yield lastUsedPromise;
		if (this.defaultChanged || bypassActivation) {
			return;
		}

		for (const connector of result) {
			if (connector && connector.isReady) {
				yield this.activate(connector, 'origin', true);
				this.defaultChanged = false;
				break;
			}
		}
	});

	private findReadyFor(chain: Chain) {
		const connectors = new Set<AnyWallet>(this.userUsedWallets);
		const isSolana = chain.mode === 'SOLANA';
		const isSui = chain.mode === 'SUI';
		for (const wallet of this.connectedWallets) {
			connectors.add(wallet);
		}
		for (const wallet of connectors) {
			if (isSui || isSolana) {
				if (wallet.chainId === chain.chainId) {
					return wallet;
				}
			} else {
				if (wallet.supports(chain)) {
					return wallet;
				}
			}
		}
		return;
	}

	fixChain(chain?: Chain) {
		return this.changeWalletChain(chain || this.root.chains.originChain);
	}
	private async changeWalletChain(chain: Chain) {
		if (isSolanaConnector(this.originWallet)) return;
		if (isSuiConnector(this.originWallet)) return;
		return this.originWallet?.changeWalletChain(chain);
	}
}

// eslint-disable-next-line @typescript-eslint/no-namespace
namespace Wallets {
	export type Connector = ConnectorEnum;
}

export default Wallets;
